import type { Project } from '@bigint-project/core';

/**
 * Undo / redo 歷史堆疊。
 *
 * 設計：每個 command 產生一組 `{ before, after }` 的完整 Project 快照。
 * 因為 `Project` 全欄位皆為 readonly、commands 一律以「替換有變動的那個
 * 陣列/欄位」的方式回傳新物件（見 commands.ts），未變動的子物件會維持同一份
 * reference，因此相鄰快照之間仍有大量結構共享，記憶體成本遠低於深拷貝。
 *
 * 這組快照本身就是「可逆的 patch」：
 * - undo：把目前狀態換回 `before`
 * - redo：把目前狀態換回 `after`
 *
 * 同性質的連續編輯（例如同一個任務的拖曳）可用 `mergeKey` 合併成一步：
 * 只要新 entry 的 `mergeKey` 與堆疊頂端相同、且在合併時間窗內，就直接
 * 覆寫頂端 entry 的 `after`，不新增一筆 history。
 */

export interface HistoryEntry {
  readonly label: string;
  readonly before: Project;
  readonly after: Project;
  readonly mergeKey: string | null;
  readonly timestamp: number;
}

export interface HistoryState {
  readonly past: readonly HistoryEntry[];
  readonly future: readonly HistoryEntry[];
}

export const HISTORY_LIMIT = 100;
/** 同一 mergeKey 在這個時間窗內的連續編輯會被合併成一步。 */
export const MERGE_WINDOW_MS = 800;

export const emptyHistory: HistoryState = { past: [], future: [] };

export interface CommitOptions {
  readonly label: string;
  /** 提供時，若與堆疊頂端相同且在時間窗內，會合併而非新增一筆歷史。 */
  readonly mergeKey?: string;
  readonly now?: number;
}

export interface CommitResult {
  readonly history: HistoryState;
  readonly merged: boolean;
}

/**
 * 將一次 project 變更記錄進歷史堆疊。
 * 呼叫端負責保證 `before`/`after` 是同一次 command 執行前後的完整 project。
 * 若 `before === after`（reference 相等，代表 command 判斷沒有實質變動），
 * 則不會產生任何歷史紀錄。
 */
export function commitHistory(
  history: HistoryState,
  before: Project,
  after: Project,
  options: CommitOptions,
): CommitResult {
  if (before === after) {
    return { history, merged: false };
  }

  const now = options.now ?? Date.now();
  const mergeKey = options.mergeKey ?? null;
  const top = history.past[history.past.length - 1];

  if (
    mergeKey !== null &&
    top !== undefined &&
    top.mergeKey === mergeKey &&
    now - top.timestamp <= MERGE_WINDOW_MS
  ) {
    const mergedEntry: HistoryEntry = {
      ...top,
      after,
      timestamp: now,
    };
    const past = history.past.slice(0, -1).concat(mergedEntry);
    return { history: { past, future: [] }, merged: true };
  }

  const entry: HistoryEntry = { label: options.label, before, after, mergeKey, timestamp: now };
  const past = history.past.concat(entry).slice(-HISTORY_LIMIT);
  return { history: { past, future: [] }, merged: false };
}

export interface UndoResult {
  readonly history: HistoryState;
  readonly project: Project;
}

export function undo(history: HistoryState, currentProject: Project): UndoResult | null {
  const entry = history.past[history.past.length - 1];
  if (entry === undefined) return null;
  const past = history.past.slice(0, -1);
  const future = [entry, ...history.future];
  void currentProject;
  return { history: { past, future }, project: entry.before };
}

export function redo(history: HistoryState, currentProject: Project): UndoResult | null {
  const entry = history.future[0];
  if (entry === undefined) return null;
  const future = history.future.slice(1);
  const past = history.past.concat(entry);
  void currentProject;
  return { history: { past, future }, project: entry.after };
}

export const canUndo = (history: HistoryState): boolean => history.past.length > 0;
export const canRedo = (history: HistoryState): boolean => history.future.length > 0;
